import { Link } from "react-router-dom";
import { SeoHead } from "@/components/SeoHead";
import { Phone, Shield, Clock, Star, MapPin, Droplets, AlertTriangle, Wrench, Filter, Zap, ArrowRight, CheckCircle2, Heart, FileText, Award, Droplet } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { PHONE_NUMBER, PHONE_HREF } from "@/components/EmergencyBanner";
import boxTruckHero from "@/assets/semper-fi-box-truck-hero.webp";
import camoTruckImg from "@/assets/semper-fi-camo-service-truck-hero.webp";
import franklinLogo from "@/assets/franklin-electric-logo.svg";
import wellCasingImg from "@/assets/well-casing-pump-installation.webp";
import grinderInstallImg from "@/assets/grinder-pump-installation-excavator.webp";
import sewagePumpImg from "@/assets/sewage-pump-basin-installation.webp";
import brandedTruckImg from "@/assets/semper-fi-branded-service-truck-full.webp";
import camoBoxTruckImg from "@/assets/semper-fi-camo-box-truck.webp";
import crewPullingImg from "@/assets/technicians-pulling-well-pump-teamwork.webp";
import crewInstallingImg from "@/assets/crew-installing-pump-basin-underground.webp";
import gravelBackfillImg from "@/assets/grinder-pump-gravel-backfill-install.webp";
import excavatorDigImg from "@/assets/kubota-excavator-well-pump-excavation.webp";
import pumpCloseupImg from "@/assets/sewage-grinder-pump-unit-closeup.webp";
import pumpBasinPipingImg from "@/assets/grinder-pump-basin-piping-connections.webp";
import pumpStationImg from "@/assets/sewage-pump-station-gravel-base.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0, 0, 0.2, 1] as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

const trustIndicators = [
  { icon: Shield, label: "Veteran Owned", sublabel: "USMC Values" },
  { icon: Clock, label: "33 Years", sublabel: "Pocono Experience" },
  { icon: Star, label: "5-Star Rated", sublabel: "450+ Reviews" },
  { icon: MapPin, label: "Local Expert", sublabel: "Born & Raised Here" },
];

const services = [
  { icon: Wrench, title: "Well Pump Repair", desc: "Fast diagnosis and repair for submersible and jet pump failures. Same-day emergency response.", href: "/services/well-pump-repair", img: wellCasingImg, imgAlt: "Well casing and pump components during repair service in the Pocono Mountains" },
  { icon: Zap, title: "Well Pump Installation", desc: "Professional installation of new well pump systems designed to last in Pocono conditions.", href: "/services/well-pump-installation", img: excavatorDigImg, imgAlt: "Kubota excavator digging for well pump installation at Pocono Mountains property" },
  { icon: AlertTriangle, title: "Grinder Pump Sales & Service", desc: "Residential and commercial grinder pump sales, installation, and repair — including commercial pump stations.", href: "/services/grinder-pump-repair", img: grinderInstallImg, imgAlt: "Excavator installing grinder pump basin at residential property" },
  { icon: Droplets, title: "Sewage Pump Systems", desc: "Complete sewage and wastewater pump service, replacement, and maintenance.", href: "/services/sewage-pump-systems", img: sewagePumpImg, imgAlt: "Sewage pump basin being lifted into position for installation" },
  { icon: Droplet, title: "Hydro-Fracking", desc: "Restore flow to low-yielding or dry wells with high-pressure hydro-fracking. Boost your well's production.", href: "/services/hydro-fracking", img: pumpStationImg, imgAlt: "Well rig and pump station equipment used for hydro-fracking low yielding wells" },
  { icon: Filter, title: "Water Filtration", desc: "Eliminate iron, sulfur, and hardness with professional filtration systems.", href: "/services/water-filtration", img: pumpCloseupImg, imgAlt: "Close-up of pump unit with pipe connections for water system service" },
  { icon: Phone, title: "Emergency Service", desc: "No water? We respond same-day. Emergency calls are always our top priority.", href: "/services/emergency-pump-service", img: camoBoxTruckImg, imgAlt: "Semper Fi Sales and Service camo-wrapped box truck for same-day emergency well, grinder, and septic pump service in the Poconos" },
];

const reviews = [
  { name: "Mike R.", location: "Stroudsburg", text: "Called at 6 AM with no water. They were at my house by 8 and had everything running by noon. Incredible service.", rating: 5 },
  { name: "Sarah T.", location: "Mount Pocono", text: "Honest, fair pricing, and they explained everything before starting work. Won't use anyone else.", rating: 5 },
  { name: "Jim K.", location: "Brodheadsville", text: "Our well pump died in January. They came out same day in terrible weather. True professionals.", rating: 5 },
  { name: "Linda P.", location: "Tobyhanna", text: "Installed a new filtration system. Water quality is night and day difference. Highly recommend.", rating: 5 },
];

const symptoms = [
  { problem: "No Water at All", desc: "Complete loss of water — likely a pump motor failure or electrical issue that needs immediate attention.", href: "/services/well-pump-repair", icon: Droplets },
  { problem: "Low Water Pressure", desc: "Weak flow or sputtering can indicate a failing pressure tank, clogged filter, or pump issue.", href: "/services/water-filtration", icon: AlertTriangle },
  { problem: "Alarm Beeping", desc: "Your grinder pump alarm signals a critical sewage system failure. Don't ignore it.", href: "/services/grinder-pump-repair", icon: Zap },
];

const faqItems = [
  { q: "How long do well pumps typically last?", a: "Most well pumps last 8 to 15 years depending on usage, water quality, and maintenance. We can inspect your system and let you know where it stands." },
  { q: "Why does my pump run constantly?", a: "A continuously running pump usually indicates a pressure switch issue, a leak in the system, or a failing pump. Call us for a diagnostic." },
  { q: "What causes low water pressure from my well?", a: "Low pressure can be caused by a failing pump, clogged filter, waterlogged pressure tank, or a drop in the water table." },
  { q: "How quickly can you respond to emergencies?", a: "We prioritize emergency no-water calls and typically respond same-day throughout the Pocono Mountains." },
];

const towns = ["Stroudsburg", "East Stroudsburg", "Mount Pocono", "Tobyhanna", "Brodheadsville", "Effort", "Blakeslee", "Tannersville", "Bushkill", "Lehighton", "Wind Gap", "Bangor", "Saylorsburg"];

const workPhotos = [
  { src: brandedTruckImg, alt: "Semper Fi Pump Sales fully branded service truck with contact details and service list" },
  { src: crewPullingImg, alt: "Semper Fi technicians working together to pull a submersible well pump for repair" },
  { src: gravelBackfillImg, alt: "Crew backfilling gravel around grinder pump basin during professional installation" },
  { src: crewInstallingImg, alt: "Semper Fi crew installing pump basin underground at residential Pocono Mountains property" },
  { src: pumpCloseupImg, alt: "Close-up of sewage grinder pump unit showing pipe connections and motor housing" },
  { src: excavatorDigImg, alt: "Kubota excavator digging for well pump and grinder pump installation" },
  { src: pumpBasinPipingImg, alt: "Grinder pump basin with discharge piping and electrical connections in excavated trench" },
  { src: pumpStationImg, alt: "Completed sewage pump station set on gravel drainage base" },
];

const Index = () => (
  <Layout>
    <SeoHead
      title="Pocono Well Pump Repair | Semper Fi Pump Service"
      description="Veteran-owned well pump, grinder pump & water system experts in the Pocono Mountains. 33 years experience, 450+ 5-star reviews. Call for same-day service."
    />
    {/* JSON-LD */}
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Semper Fi Well & Pump Services",
      "address": { "@type": "PostalAddress", "streetAddress": "119 Lacey Ln", "addressLocality": "Saylorsburg", "addressRegion": "PA", "postalCode": "18353", "addressCountry": "US" },
      "description": "The Premier Pocono Mountains Well & Pump Service. Veteran-owned with 33 years of experience repairing and installing well pumps, grinder pumps, and water systems. Factory-authorized Franklin Electric distributor and warranty center.",
      "areaServed": ["Monroe County", "Pike County", "Carbon County", "Northampton County"],
      "priceRange": "$$",
      "foundingDate": "1992",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "450" },
      "telephone": "+18664241863"
    })}} />

    {/* ═══════════ HERO — FULL VIEWPORT ═══════════ */}
    <section className="relative min-h-[75vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={boxTruckHero}
          alt="Semper Fi Sales and Service branded box truck — veteran-owned well pump, grinder pump, and septic pump service in the Pocono Mountains"
          width={1600}
          height={900}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />
      </div>
      <div className="relative container py-24 md:py-32">
        <motion.div
          className="max-w-3xl"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          {/* Rating badge */}
          <motion.div variants={fadeUp} className="inline-flex items-center gap-3 bg-background/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-primary-foreground/15">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-accent text-accent" />)}
            </div>
            <span className="text-primary-foreground text-xs font-semibold">Trusted by 10,000+ Happy Customers</span>
          </motion.div>

          <motion.h1 variants={fadeUp} className="font-extrabold text-primary-foreground mb-6 tracking-tight font-display text-[1.75rem] md:text-[2.25rem] lg:text-[2.75rem] xl:text-[3.25rem] leading-[1.15]">
            <span className="block">The Premier Pocono Mountains Well &amp; Pump Service</span>
            <span className="block text-accent">From Veterans You Can Trust</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg md:text-xl text-primary-foreground/70 text-pretty leading-relaxed max-w-xl">
            Fast emergency response for well pump failures and residential & commercial grinder pump systems. 33 years serving Monroe, Pike, Carbon, and Northampton Counties.
          </motion.p>
        </motion.div>
      </div>
    </section>

    {/* ═══════════ TRUST BAR ═══════════ */}
    <section className="relative -mt-12 z-10">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-background rounded-2xl shadow-elevated grid grid-cols-2 lg:grid-cols-4 border border-border"
        >
          {trustIndicators.map((item, i) => (
            <div key={item.label} className={`flex items-center gap-3 px-5 py-6 lg:px-7 lg:py-7 ${i % 2 === 0 ? "border-r border-border" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i < trustIndicators.length - 1 ? "lg:border-r lg:border-border" : ""}`}>
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                <item.icon className="w-5 h-5 text-accent" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground leading-tight">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.sublabel}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* ═══════════ FRANKLIN ELECTRIC AUTHORIZATION ═══════════ */}
    <section className="container pt-16 md:pt-20">
      <motion.a
        href="https://www.franklin-electric.com/"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="block bg-card border border-border rounded-2xl p-6 md:p-8 shadow-card hover:shadow-elevated transition-shadow"
      >
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className="shrink-0 bg-background rounded-xl p-4 border border-border w-full md:w-auto flex items-center justify-center">
            <img src={franklinLogo} alt="Franklin Electric logo" className="h-10 md:h-12 w-auto" loading="lazy" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-accent" />
              <span className="text-xs font-bold uppercase tracking-widest text-accent">Authorized Partner</span>
            </div>
            <h2 className="text-xl md:text-2xl font-extrabold font-display mb-2 text-foreground">
              Factory Authorized Franklin Electric Distributor & Warranty Center
            </h2>
            <p className="text-sm text-muted-foreground text-pretty leading-relaxed">
              We are a factory-authorized distributor and warranty center for Franklin Electric pumps — the industry's most trusted name in residential and commercial pumping systems. Genuine parts, expert installation, and full manufacturer-backed warranty support on every job.
            </p>
          </div>
        </div>
      </motion.a>
    </section>

    {/* ═══════════ WHO WE ARE ═══════════ */}
    <section className="container py-24 md:py-32">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">About Us</motion.span>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold mb-6 font-display leading-tight">
            A Marine's Commitment<br />to Your Water
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-pretty leading-relaxed mb-4">
            Founded by <strong className="text-foreground">Joby Poster</strong> — a six-year U.S. Marine Corps veteran who served in <strong className="text-foreground">Operation Desert Shield</strong> and <strong className="text-foreground">Operation Desert Storm</strong> — Semper Fi Well & Pump Services was built on the same principles that guide every Marine: integrity, discipline, and an unwavering commitment to getting the job done right. Joby brought those values home to the Pocono Mountains where he was born and raised.
          </motion.p>
          <motion.p variants={fadeUp} className="text-muted-foreground text-pretty leading-relaxed mb-4">
            With 33 years of hands-on experience repairing, installing, and maintaining well pumps, grinder pumps, and water systems across Monroe, Pike, Carbon, and Northampton Counties, we've earned the trust of more than 10,000 Pocono homeowners and businesses.
          </motion.p>
          <motion.p variants={fadeUp} className="text-muted-foreground text-pretty leading-relaxed mb-8 italic border-l-2 border-accent/30 pl-4">
            "Semper Fidelis" means "Always Faithful" — faithful to our word, faithful to our community, and faithful to delivering honest, reliable service every single time.
          </motion.p>
          <motion.div variants={fadeUp} className="grid grid-cols-2 gap-5">
            {[
              { icon: Shield, title: "Veteran Integrity", desc: "USMC values guide every job." },
              { icon: MapPin, title: "Local Roots", desc: "Born and raised in the Poconos." },
              { icon: Wrench, title: "33 Years", desc: "10,000+ systems serviced." },
              { icon: Heart, title: "Community First", desc: "Your neighbor, not a corporation." },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
                <item.icon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="space-y-4"
        >
          <div className="rounded-2xl overflow-hidden shadow-elevated aspect-[16/10]">
            <img src={camoTruckImg} alt="Camouflage-wrapped Semper Fi Sales and Service truck at a Pocono Mountains home for well pump, grinder pump, and septic pump service" width={1182} height={879} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl overflow-hidden shadow-card aspect-[4/3]">
              <img src={crewPullingImg} alt="Semper Fi technicians pulling a submersible well pump for inspection and repair" width={400} height={300} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <div className="rounded-xl overflow-hidden shadow-card aspect-[4/3]">
              <img src={crewInstallingImg} alt="Semper Fi crew members installing a pump basin below grade at residential property" width={400} height={300} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ═══════════ WHAT'S THE PROBLEM? ═══════════ */}
    <section className="bg-secondary/50">
      <div className="container py-24 md:py-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="max-w-xl mb-14"
        >
          <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">Troubleshooting</motion.span>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">What's the Problem?</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-pretty">
            Identify your symptom below. We'll get you to the right solution fast.
          </motion.p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-3 gap-5"
        >
          {symptoms.map((s) => (
            <motion.div key={s.problem} variants={fadeUp} className="flex">
              <Link
                to={s.href}
                className="group relative bg-background rounded-2xl p-8 shadow-card hover:shadow-elevated flex flex-col border border-border hover:border-accent/20 transition-all duration-300 w-full"
              >
                <div className="w-14 h-14 rounded-xl gradient-accent flex items-center justify-center mb-6 shadow-glow">
                  <s.icon className="w-6 h-6 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-accent transition-colors font-display">{s.problem}</h3>
                <p className="text-sm text-muted-foreground text-pretty mb-5 leading-relaxed flex-1">{s.desc}</p>
                <span className="text-sm font-bold text-accent inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all mt-auto">
                  Get Help <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* ═══════════ SERVICES ═══════════ */}
    <section className="container py-24 md:py-32">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="max-w-xl mb-14"
      >
        <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">What We Do</motion.span>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">Our Services</motion.h2>
        <motion.p variants={fadeUp} className="text-muted-foreground text-pretty">
          Complete well pump and water system services for homes across the Pocono Mountains.
        </motion.p>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        {services.map((service) => (
          <motion.div key={service.title} variants={fadeUp} className="flex">
            <Link
              to={service.href}
              className="group bg-background rounded-2xl shadow-card hover:shadow-elevated flex flex-col border border-border hover:border-accent/20 overflow-hidden transition-all duration-300 w-full"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={service.img}
                  alt={service.imgAlt}
                  width={400}
                  height={250}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <service.icon className="w-4 h-4 text-accent" />
                  </div>
                  <h3 className="font-bold text-foreground group-hover:text-accent transition-colors font-display">{service.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground text-pretty leading-relaxed flex-1">{service.desc}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </section>

    {/* ═══════════ EMERGENCY CTA ═══════════ */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={gravelBackfillImg} alt="Pump installation work in progress" width={1600} height={900} className="w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-primary/90" />
      </div>
      <div className="container py-24 md:py-32 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">Emergency Service</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold text-primary-foreground mb-5 font-display leading-tight">
              No Water?<br />Don't Wait.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-pretty leading-relaxed mb-8 text-lg">
              Pump failures don't wait for business hours. When you turn on the tap and nothing comes out, you need a technician who knows your local wells and can get there fast.
            </motion.p>
            <motion.ul variants={fadeUp} className="space-y-4 mb-10">
              {[
                "Same-day emergency response",
                "Residential & commercial grinder pump experts",
                "All pump types: submersible, jet, grinder, sewage",
                "Serving a 60-mile radius from Saylorsburg",
                "Honest diagnosis — no unnecessary upselling",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-primary-foreground/80 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  {item}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
              <Button asChild variant="hero" size="xl">
                <a href={PHONE_HREF}>
                  <Phone className="w-5 h-5" />
                  Get Water Back Now
                </a>
              </Button>
              <Button asChild variant="heroOutline" size="xl">
                <Link to="/contact">
                  <FileText className="w-5 h-5" />
                  Free Quote
                </Link>
              </Button>
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="hidden lg:block"
          >
            <div className="rounded-2xl overflow-hidden shadow-hero border-4 border-primary-foreground/10 aspect-[4/3]">
              <img
                src={pumpBasinPipingImg}
                alt="Grinder pump basin with discharge piping connections during professional installation"
                width={600}
                height={450}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ═══════════ WORK GALLERY ═══════════ */}
    <section className="container py-24 md:py-32">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="max-w-xl mb-12"
      >
        <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">Our Work</motion.span>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">Work in the Field</motion.h2>
        <motion.p variants={fadeUp} className="text-muted-foreground text-pretty">Real jobs. Real results. See the quality of our pump installation and repair work.</motion.p>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {workPhotos.map((photo, i) => (
          <motion.div key={i} variants={fadeUp} className="aspect-[4/3] rounded-xl overflow-hidden shadow-card group">
            <img src={photo.src} alt={photo.alt} width={400} height={300} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
          </motion.div>
        ))}
      </motion.div>
    </section>

    {/* ═══════════ REVIEWS ═══════════ */}
    <section className="bg-secondary/50">
      <div className="container py-24 md:py-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14"
        >
          <div className="max-w-xl">
            <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">Testimonials</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">What Our Customers Say</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-pretty">Real reviews from homeowners across the Pocono Mountains.</motion.p>
          </div>
          <motion.div variants={fadeUp} className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-accent text-accent" />)}
            </div>
            <span className="text-sm font-bold tabular-nums">5.0</span>
            <span className="text-xs text-muted-foreground">(450+ 5-star reviews)</span>
          </motion.div>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-5"
        >
          {reviews.map((review, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-background rounded-2xl p-8 shadow-card border border-border flex flex-col">
              <div className="flex gap-0.5 mb-5">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-foreground text-pretty leading-relaxed mb-6 flex-1">"{review.text}"</p>
              <div className="flex items-center gap-3 mt-auto">
                <div className="w-10 h-10 rounded-full gradient-accent flex items-center justify-center text-accent-foreground text-sm font-bold shadow-sm shrink-0">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{review.name}</p>
                  <p className="text-xs text-muted-foreground">{review.location}, PA</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center mt-12">
          <Button asChild variant="outline" size="lg">
            <Link to="/reviews">See All Reviews <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </div>
    </section>

    {/* ═══════════ SERVICE AREA ═══════════ */}
    <section className="container py-24 md:py-32">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
      >
        <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">Coverage</motion.span>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">Serving the Pocono Mountains</motion.h2>
        <motion.p variants={fadeUp} className="text-muted-foreground text-pretty mb-10 max-w-xl">
          Monroe, Pike, Carbon, and Northampton Counties — we know these wells because we've been repairing them for decades.
        </motion.p>
        <motion.div variants={fadeUp} className="flex flex-wrap gap-2.5 mb-10">
          {towns.map((town) => (
            <span key={town} className="bg-secondary text-sm font-medium text-foreground px-4 py-2.5 rounded-lg border border-border">{town}</span>
          ))}
        </motion.div>
        <motion.div variants={fadeUp}>
          <Button asChild variant="outline" size="lg">
            <Link to="/service-area">View Full Service Area <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </motion.div>
      </motion.div>
    </section>

    {/* ═══════════ FAQ ═══════════ */}
    <section className="bg-secondary/50">
      <div className="container py-24 md:py-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.span variants={fadeUp} className="text-xs font-bold uppercase tracking-widest text-accent mb-4 block">FAQ</motion.span>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-extrabold font-display mb-3">Frequently Asked Questions</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-pretty mb-14 max-w-xl">Quick answers to the most common questions we hear from homeowners.</motion.p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-5 max-w-4xl"
        >
          {faqItems.map((item, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-background rounded-2xl p-8 shadow-card border border-border">
              <h3 className="font-bold text-foreground mb-3 font-display">{item.q}</h3>
              <p className="text-sm text-muted-foreground text-pretty leading-relaxed">{item.a}</p>
            </motion.div>
          ))}
        </motion.div>
        <div className="mt-10">
          <Button asChild variant="outline" size="lg">
            <Link to="/faq">View All FAQs <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  </Layout>
);

export default Index;
